import React, { useState, Fragment } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { styled } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import Button from '@atlaskit/button';
import Form, { Field, HelperMessage, ErrorMessage } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';

import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { fg } from '@confluence/feature-gating';

import { PRIMARY_COLORS } from '../colors.ts';
import { sharedi18n } from '../sharedi18n.ts';

import { LargeColorCircle } from './LargeColorCircle.tsx';
import type { ContentStateMakerProps } from './types.ts';
import { i18n } from './i18n.ts';

const contentStateMakerWrapperStyles = xcss({
	padding: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CirclesWrapper = styled.div({
	display: 'flex',
	marginBottom: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonsWrapper = styled.div({
	display: 'flex',
	justifyContent: 'flex-end',
});

const circleAndButtonsWrapperStyles = xcss({
	marginTop: 'space.100',
});

const ContentStateMakerComponent = withFlags(
	({
		spaceKey,
		color,
		name,
		closePopup,
		onSave,
		flags,
		intl: { formatMessage },
	}: ContentStateMakerProps) => {
		const [selectedColor, setSelectedColor] = useState<string>(color);
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const colorsArray = [
			PRIMARY_COLORS.PURPLE,
			PRIMARY_COLORS.BLUE,
			PRIMARY_COLORS.RED,
			PRIMARY_COLORS.YELLOW,
			PRIMARY_COLORS.GREEN,
		];

		const ColorCircles = colorsArray.map((circleColor) => {
			return (
				<LargeColorCircle
					key={circleColor}
					color={circleColor}
					selectedColor={selectedColor}
					setSelectedColor={setSelectedColor}
				/>
			);
		});

		const handleSubmit = (formState: { input: string }) => {
			closePopup();
			// only save/mutate if the user inputs a status - don't need to check if state has color since default is purple
			if (formState.input) {
				// remove any extra whitespace in string
				const trimmedName = formState.input.replace(/\s+/g, ' ').trim();
				// make sure user didn't enter only whitespace
				if (trimmedName) {
					onSave(selectedColor, trimmedName);
				} else {
					// user submits all whitespace
					void flags.showWarningFlag({
						title: <FormattedMessage {...sharedi18n.emptyStatusFlagTitle} />,
						description: <FormattedMessage {...sharedi18n.emptyStatusFlagDescription} />,
					});
				}
			}

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					objectId: spaceKey,
					actionSubjectId: 'adminClickedSaveButton',
					source: 'contentStateAdminSettings',
				},
			}).fire();
		};

		const validateName = (value: string | undefined) => {
			if (!value) return 'REQUIRED'; // Name input is required
			return undefined;
		};

		return (
			<Box xcss={contentStateMakerWrapperStyles}>
				{fg('platform-design_system_team-form_conversion') ? (
					<Form onSubmit={handleSubmit} name="content-state-maker">
						<Field name="input" defaultValue={name} validate={validateName}>
							{({ fieldProps, error }: any) => (
								<Fragment>
									<Textfield
										{...fieldProps}
										testId="text-input"
										autocomplete="off"
										maxLength={20}
										placeholder={formatMessage(i18n.addContentStatePlaceholder)}
									/>
									{error === 'REQUIRED' && (
										<ErrorMessage>{formatMessage(sharedi18n.requiredErrorMessage)}</ErrorMessage>
									)}
									{!error && <HelperMessage>{formatMessage(sharedi18n.maxLength)}</HelperMessage>}
								</Fragment>
							)}
						</Field>
						<Box xcss={circleAndButtonsWrapperStyles}>
							<CirclesWrapper>{ColorCircles}</CirclesWrapper>
							<ButtonsWrapper>
								<Button
									testId="cancel-button"
									appearance="subtle"
									onClick={closePopup}
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
									style={{ marginRight: token('space.050') }}
								>
									{formatMessage(i18n.cancel)}
								</Button>
								<Button type="submit" appearance="primary" testId="save-button">
									{formatMessage(i18n.save)}
								</Button>
							</ButtonsWrapper>
						</Box>
					</Form>
				) : (
					<Form onSubmit={handleSubmit}>
						{({ formProps }) => (
							<form {...formProps} name="content-state-maker">
								<Field name="input" defaultValue={name} validate={validateName}>
									{({ fieldProps, error }: any) => (
										<Fragment>
											<Textfield
												{...fieldProps}
												testId="text-input"
												autocomplete="off"
												maxLength={20}
												placeholder={formatMessage(i18n.addContentStatePlaceholder)}
											/>
											{error === 'REQUIRED' && (
												<ErrorMessage>
													{formatMessage(sharedi18n.requiredErrorMessage)}
												</ErrorMessage>
											)}
											{!error && (
												<HelperMessage>{formatMessage(sharedi18n.maxLength)}</HelperMessage>
											)}
										</Fragment>
									)}
								</Field>
								<Box xcss={circleAndButtonsWrapperStyles}>
									<CirclesWrapper>{ColorCircles}</CirclesWrapper>
									<ButtonsWrapper>
										<Button
											testId="cancel-button"
											appearance="subtle"
											onClick={closePopup}
											// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
											style={{ marginRight: token('space.050') }}
										>
											{formatMessage(i18n.cancel)}
										</Button>
										<Button type="submit" appearance="primary" testId="save-button">
											{formatMessage(i18n.save)}
										</Button>
									</ButtonsWrapper>
								</Box>
							</form>
						)}
					</Form>
				)}
			</Box>
		);
	},
);

export const ContentStateMaker = injectIntl(ContentStateMakerComponent);
